import React, { useState, useEffect, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import axios from 'axios';
import { useNavigate, useOutletContext } from 'react-router-dom';
import NoAccess from '../components/NoAccess';
import { gridTheme, defaultColDef, centeredCellStyle, rightAlignedCellStyle } from '../services/aggrid';
import { Panel, Chip, Btn, PageHead, I } from '../components/hud';

axios.defaults.withCredentials = true;

const API_BASE_URL = (import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:5000')) + '/api';

function Assets() {
  const navigate = useNavigate();
  const { user } = useOutletContext();
  const [assets, setAssets] = useState([]);

  if (!user?.permissions?.canViewAssets) {
    return <NoAccess feature="Corporation Assets" requiredRole="Director or CEO" />;
  }
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [syncMessage, setSyncMessage] = useState(null);
  const [quickFilter, setQuickFilter] = useState('');

  const fetchAssets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/assets`);
      setAssets(response.data);
    } catch (err) {
      console.error('Error fetching assets:', err);
      if (err.response?.data?.requiresReauth || err.response?.status === 401) {
        navigate('/?error=session_expired');
        return;
      }
      setError(err.response?.data?.error || 'Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSyncMessage(null);
      const response = await axios.post(`${API_BASE_URL}/assets/sync`);
      setSyncMessage({ type: 'success', message: response.data.message });
      await fetchAssets();
    } catch (err) {
      console.error('Error syncing assets:', err);
      if (err.response?.data?.requiresReauth || err.response?.status === 401) {
        navigate('/?error=session_expired');
        return;
      }
      const errorMessage = err.response?.data?.error || 'Failed to sync assets';
      setError(errorMessage);
      setSyncMessage({ type: 'error', message: errorMessage });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { fetchAssets(); }, []);

  const formatNumber = (num) => new Intl.NumberFormat('en-US').format(num);

  // Telemetry — derived only from real data fields
  const uniqueTypes = useMemo(() => new Set(assets.map(a => a.typeName)).size, [assets]);
  const uniqueLocations = useMemo(() => new Set(assets.map(a => a.locationName)).size, [assets]);

  const columnDefs = useMemo(() => [
    {
      field: 'typeName',
      headerName: 'Item Name',
      flex: 2,
      minWidth: 240,
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => (
        <span className="cell-strong">{params.value || 'Unknown'}</span>
      )
    },
    {
      field: 'locationName',
      headerName: 'Location',
      flex: 2,
      minWidth: 220,
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => (
        <span className="cell-mute">{params.value || 'Unknown'}</span>
      )
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      width: 160,
      filter: 'agNumberColumnFilter',
      cellStyle: rightAlignedCellStyle,
      cellRenderer: (params) => (
        <span className="num">{formatNumber(params.value)}</span>
      )
    },
    {
      field: 'locationType',
      headerName: 'Type',
      width: 140,
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      valueGetter: (p) => {
        switch (p.data.locationType) {
          case 'station': return 'Station';
          case 'structure': return 'Structure';
          case 'solar_system': return 'Space';
          default: return 'Other';
        }
      },
      cellRenderer: (params) => (
        <span className="cell-mute">{params.value}</span>
      )
    }
  ], []);

  return (
    <>
      <PageHead
        crumb="OPS // INVENTORY"
        title="Corporation Assets"
        actions={
          <Btn icon={I.Sync} tone="primary" onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync from ESI'}
          </Btn>
        }
      />

      <div className="telemetry-strip">
        <div className="tile ok">
          <div className="k">UNIQUE TYPES</div>
          <div className="v">{uniqueTypes}<span className="unit">ITEMS</span></div>
        </div>
        <div className="tile amber">
          <div className="k">LOCATIONS</div>
          <div className="v">{uniqueLocations}<span className="unit">STATIONS</span></div>
        </div>
      </div>

      {syncMessage && (
        <div className={`label ${syncMessage.type === 'error' ? 'danger' : 'ok'}`} style={{ marginBottom: 12 }}>
          {syncMessage.message}
          <button
            onClick={() => setSyncMessage(null)}
            style={{ marginLeft: 12, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="label danger" style={{ marginBottom: 12 }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{ marginLeft: 12, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <span className="cell-mute mono">LOADING ASSET DATA…</span>
        </div>
      ) : assets.length === 0 ? (
        <Panel title="Asset Ledger">
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p className="cell-mute" style={{ marginBottom: 16 }}>No assets found. Sync from ESI to populate.</p>
            <Btn icon={I.Sync} tone="primary" onClick={handleSync} disabled={syncing}>
              Sync Assets
            </Btn>
          </div>
        </Panel>
      ) : (
        <Panel
          title="Asset Ledger"
          sub={`${assets.length} ROWS`}
        >
          <div className="filter-bar" style={{ marginBottom: 12 }}>
            <input
              className="hud-input search"
              placeholder="Quick search…"
              value={quickFilter}
              onChange={(e) => setQuickFilter(e.target.value)}
            />
          </div>
          <div className="hud-grid">
            <AgGridReact
              theme={gridTheme}
              rowData={assets}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              domLayout="autoHeight"
              rowHeight={48}
              headerHeight={40}
              floatingFiltersHeight={36}
              pagination
              paginationPageSize={50}
              paginationPageSizeSelector={[25, 50, 100]}
              quickFilterText={quickFilter}
              getRowId={(params) => params.data.itemId?.toString() || `${params.data.typeName}-${params.data.locationName}-${params.data.quantity}`}
              animateRows
            />
          </div>
        </Panel>
      )}
    </>
  );
}

export default Assets;
